/**
 * MirrorStage — The immersive background environment.
 *
 * 4-layer depth system:
 *  1. Ambient glow (outermost radial light)
 *  2. Bloom halo (focused glow around avatar position)
 *  3. Vertical beam (subtle light column)
 *  4. Light sweep (slow horizontal shimmer)
 *
 * All layers react to the current PresenceVisuals config via CSS transitions.
 */

import { PresenceVisuals, MIRROR_STAGE_BG } from "./MirrorPresenceConfig";

interface MirrorStageProps {
  visuals: PresenceVisuals;
}

const MirrorStage = ({ visuals }: MirrorStageProps) => {
  const t = `${visuals.transitionDuration}s ease`;

  return (
    <div className="absolute inset-0 z-0 overflow-hidden" style={{ backgroundColor: MIRROR_STAGE_BG }}>

      {/* Layer 1 — Ambient glow */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 80% 70% at 50% 42%, hsl(${visuals.haloHue} 60% 50% / ${visuals.stageAmbient}), transparent 70%)`,
          transition: `all ${t}`,
        }}
      />

      {/* Layer 2 — Vignette / depth */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 55% 50% at 50% 50%, transparent 30%, ${MIRROR_STAGE_BG} ${Math.round(visuals.stageVignette * 100)}%)`,
          transition: `all ${t}`,
        }}
      />

      {/* Layer 3 — Vertical beam behind avatar */}
      <div
        className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[120px]"
        style={{
          background: `linear-gradient(180deg, transparent 0%, hsl(${visuals.haloHue} 60% 50% / 0.04) 20%, hsl(${visuals.haloHue} 60% 50% / 0.02) 50%, hsl(${visuals.haloHue} 60% 50% / 0.04) 80%, transparent 100%)`,
          opacity: visuals.beamOpacity,
          transition: `opacity ${t}`,
        }}
      />

      {/* Layer 4 — Light sweep shimmer */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(100deg, transparent 38%, hsl(${visuals.haloHue} 55% 60% / 0.025) 45%, hsl(${visuals.haloHue} 55% 60% / 0.04) 50%, transparent 62%)`,
          animation: `mirror-sweep ${visuals.sweepDuration}s ease-in-out infinite`,
          transition: `opacity ${t}`,
        }}
      />

      {/* Floor reflection gradient */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[35%]"
        style={{
          background: `linear-gradient(to top, hsl(${visuals.haloHue} 50% 45% / ${visuals.floorReflection}), transparent)`,
          transition: `all ${t}`,
        }}
      />

      {/* Portal arch — outer diffuse glow */}
      <div
        className="absolute top-[4%] left-1/2 -translate-x-1/2 w-[320px] h-[560px] rounded-[50%] pointer-events-none"
        style={{
          boxShadow: `0 0 120px 40px hsl(${visuals.haloHue} 70% 55% / ${visuals.haloIntensity * 0.22})`,
          transition: `box-shadow ${t}`,
        }}
      />

      {/* Portal arch — crisp rim ring */}
      <div
        className="absolute top-[4%] left-1/2 -translate-x-1/2 w-[300px] h-[530px] rounded-[50%] pointer-events-none"
        style={{
          border: `1px solid hsl(${visuals.haloHue} 75% 65% / ${visuals.haloIntensity * 0.45})`,
          boxShadow: `0 0 28px 6px hsl(${visuals.haloHue} 70% 60% / ${visuals.haloIntensity * 0.18}), inset 0 0 40px 8px hsl(${visuals.haloHue} 65% 55% / ${visuals.haloIntensity * 0.10})`,
          transition: `border-color ${t}, box-shadow ${t}`,
        }}
      />

      {/* Portal arch — inner surface shimmer */}
      <div
        className="absolute top-[4%] left-1/2 -translate-x-1/2 w-[300px] h-[530px] rounded-[50%] pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 60% 55% at 50% 38%, hsl(${visuals.haloHue} 60% 55% / ${visuals.haloIntensity * 0.07}), transparent 70%)`,
          transition: `background ${t}`,
        }}
      />
    </div>
  );
};

export default MirrorStage;
